import { useState, useRef, useCallback, useEffect } from 'react';

const DEFAULT_MIN_ZOOM = 0.2;
const DEFAULT_MAX_ZOOM = 5;
const WHEEL_ZOOM_OUT_FACTOR = 0.9;
const WHEEL_ZOOM_IN_FACTOR = 1.1;
const BUTTON_ZOOM_STEP = 0.2;

/**
 * Shared hook for image preview panels with scroll-to-zoom, drag-to-pan,
 * and optional rotate.
 *
 * Usage:
 *   const viewer = useImageViewer();
 *
 *   <div ref={viewer.containerRef} {...viewer.panHandlers} style={viewer.containerStyle}>
 *     <img style={{ transform: viewer.imageTransform, transformOrigin: 'center center' }} />
 *     {/* floating toolbar *\/}
 *     <button onClick={viewer.zoomIn}>+</button>
 *     <button onClick={viewer.zoomOut}>-</button>
 *     <button onClick={viewer.rotate}>↻</button>
 *     <button onClick={viewer.reset}>Reset</button>
 *   </div>
 *
 * Wheel zoom uses a native { passive: false } listener (via callback ref)
 * because React's synthetic onWheel is registered as passive — calling
 * preventDefault() is silently ignored, letting the browser's Ctrl+Scroll
 * page-zoom fire instead of our custom zoom.
 */
const getTouchDistance = (t1, t2) => {
  const dx = t1.clientX - t2.clientX;
  const dy = t1.clientY - t2.clientY;
  return Math.sqrt(dx * dx + dy * dy);
};

export function useImageViewer({ minZoom = DEFAULT_MIN_ZOOM, maxZoom = DEFAULT_MAX_ZOOM } = {}) {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [isPanning, setIsPanning] = useState(false);

  const dragStart = useRef({ x: 0, y: 0 });
  const nodeRef = useRef(null);
  const wheelRef = useRef(null);
  const configRef = useRef({ minZoom, maxZoom });
  configRef.current = { minZoom, maxZoom };

  // Mirror `pan` in a ref so the window-level drag handlers always read the
  // latest offset without being re-created (avoids stale-closure panning).
  const panRef = useRef({ x: 0, y: 0 });
  panRef.current = pan;

  // Active window listeners for an in-flight mouse drag, so we can detach them
  // on drag end AND on unmount (prevents leaks if the modal closes mid-drag).
  const winMoveRef = useRef(null);
  const winUpRef = useRef(null);

  const lastPinchDistRef = useRef(null);
  const isPinchingRef = useRef(false);

  // Callback ref — attaches/detaches the native wheel listener automatically
  // when the DOM node mounts/unmounts (handles conditional rendering).
  const containerRef = useCallback((node) => {
    if (nodeRef.current && wheelRef.current) {
      nodeRef.current.removeEventListener('wheel', wheelRef.current);
    }
    nodeRef.current = node;
    if (node) {
      const handler = (e) => {
        e.preventDefault();
        const { minZoom: min, maxZoom: max } = configRef.current;
        const factor = e.deltaY > 0 ? WHEEL_ZOOM_OUT_FACTOR : WHEEL_ZOOM_IN_FACTOR;
        setZoom(z => Math.min(max, Math.max(min, z * factor)));
      };
      wheelRef.current = handler;
      node.addEventListener('wheel', handler, { passive: false });
    }
  }, []);

  const endMouseDrag = useCallback(() => {
    if (winMoveRef.current) window.removeEventListener('mousemove', winMoveRef.current);
    if (winUpRef.current) window.removeEventListener('mouseup', winUpRef.current);
    winMoveRef.current = null;
    winUpRef.current = null;
    document.body.style.userSelect = '';
    setIsPanning(false);
  }, []);

  const panHandlers = {
    // Only the drag START is bound to the element. Move/end are bound to
    // `window` for the duration of the drag so panning keeps tracking the
    // cursor even when it leaves the preview box (over the toolbar, the form
    // panel, or off-screen). The previous element-level onMouseMove/onMouseLeave
    // stopped the drag the instant the cursor crossed the container edge —
    // which, on a fit-to-panel document, is almost immediately.
    onMouseDown: (e) => {
      if (e.button !== 0) return; // primary button only
      e.preventDefault();
      setIsPanning(true);
      dragStart.current = { x: e.clientX - panRef.current.x, y: e.clientY - panRef.current.y };
      // Suppress text selection on the panels while dragging.
      document.body.style.userSelect = 'none';

      const onMove = (ev) => {
        setPan({ x: ev.clientX - dragStart.current.x, y: ev.clientY - dragStart.current.y });
      };
      const onUp = () => endMouseDrag();

      winMoveRef.current = onMove;
      winUpRef.current = onUp;
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    onTouchStart: (e) => {
      if (e.touches.length === 2) {
        isPinchingRef.current = true;
        setIsPanning(false);
        lastPinchDistRef.current = getTouchDistance(e.touches[0], e.touches[1]);
      } else if (e.touches.length === 1 && !isPinchingRef.current) {
        const t = e.touches[0];
        setIsPanning(true);
        dragStart.current = { x: t.clientX - pan.x, y: t.clientY - pan.y };
      }
    },
    onTouchMove: (e) => {
      e.preventDefault();
      if (e.touches.length === 2) {
        const dist = getTouchDistance(e.touches[0], e.touches[1]);
        if (lastPinchDistRef.current !== null) {
          const { minZoom: min, maxZoom: max } = configRef.current;
          const scale = dist / lastPinchDistRef.current;
          setZoom(z => Math.min(max, Math.max(min, z * scale)));
        }
        lastPinchDistRef.current = dist;
      } else if (e.touches.length === 1 && isPanning && !isPinchingRef.current) {
        const t = e.touches[0];
        setPan({ x: t.clientX - dragStart.current.x, y: t.clientY - dragStart.current.y });
      }
    },
    onTouchEnd: (e) => {
      if (e.touches.length < 2) {
        isPinchingRef.current = false;
        lastPinchDistRef.current = null;
      }
      if (e.touches.length === 0) {
        setIsPanning(false);
      }
    },
  };

  // Detach any in-flight drag listeners if the host modal unmounts mid-drag.
  useEffect(() => endMouseDrag, [endMouseDrag]);

  const containerStyle = {
    cursor: isPanning ? 'grabbing' : 'grab',
    touchAction: 'none',
  };

  const imageTransform = `translate(${pan.x}px, ${pan.y}px) scale(${zoom}) rotate(${rotation}deg)`;

  const zoomIn = useCallback(() => {
    setZoom(z => Math.min(configRef.current.maxZoom, z + BUTTON_ZOOM_STEP));
  }, []);

  const zoomOut = useCallback(() => {
    setZoom(z => Math.max(configRef.current.minZoom, z - BUTTON_ZOOM_STEP));
  }, []);

  const rotate = useCallback(() => {
    setRotation(r => (r + 90) % 360);
    setPan({ x: 0, y: 0 });
  }, []);

  const reset = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setRotation(0);
  }, []);

  return {
    zoom,
    setZoom,
    pan,
    rotation,
    isPanning,
    containerRef,
    panHandlers,
    containerStyle,
    imageTransform,
    zoomIn,
    zoomOut,
    rotate,
    reset,
  };
}
